/**
 * Utilitário para comunicação com o app nativo via deep links
 * NÃO altera funcionalidades existentes - apenas adiciona opção de usar app nativo
 */

const NATIVE_APP_SCHEME = 'nexusmotorista';

export interface NativeAppTrackingParams {
  osId: number;
  faseId: number;
  token: string;
  action: 'start' | 'stop';
}

/**
 * Tenta abrir o app nativo via deep link
 * Se o app não estiver instalado, retorna false
 */
export const openNativeApp = (params: NativeAppTrackingParams): boolean => {
  try {
    const { osId, faseId, token, action } = params;
    
    // Criar URL do deep link
    const deepLinkUrl = `${NATIVE_APP_SCHEME}://track?osId=${osId}&faseId=${faseId}&token=${encodeURIComponent(token)}&action=${action}`;
    
    console.log('🔗 Tentando abrir app nativo:', deepLinkUrl);
    console.log('📱 Parâmetros:', { osId, faseId, action, tokenLength: token?.length });
    
    // Detectar se está em dispositivo móvel
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Em mobile, tentar múltiplas abordagens
      try {
        // Método 1: window.location (mais comum)
        window.location.href = deepLinkUrl;
        console.log('✅ Deep link enviado via window.location.href');
      } catch (e) {
        try {
          // Método 2: window.open (fallback)
          window.open(deepLinkUrl, '_blank');
          console.log('✅ Deep link enviado via window.open');
        } catch (e2) {
          // Método 3: criar link temporário e clicar
          const link = document.createElement('a');
          link.href = deepLinkUrl;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('✅ Deep link enviado via link.click()');
        }
      }
    } else {
      // Em desktop, usar window.location
      window.location.href = deepLinkUrl;
      console.log('✅ Deep link enviado (desktop)');
    }
    
    // Verificar se o app foi aberto (após um tempo, se ainda estiver na página, provavelmente não foi instalado)
    setTimeout(() => {
      console.log('ℹ️ Se o app nativo não abriu, verifique se está instalado');
    }, 1000);
    
    return true;
  } catch (error) {
    console.error('❌ Erro ao tentar abrir app nativo:', error);
    return false;
  }
};

/**
 * Verifica se o app nativo está disponível
 * (tentativa de detectar - nem sempre é 100% confiável)
 */
export const isNativeAppAvailable = (): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      const testUrl = `${NATIVE_APP_SCHEME}://test`;
      const startTime = Date.now();
      let opened = false;

      // Tentar abrir
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = testUrl;
      document.body.appendChild(iframe);

      // Se o app abrir, o iframe será ignorado
      // Se não abrir, o iframe tentará carregar e falhar
      setTimeout(() => {
        document.body.removeChild(iframe);
        
        // Se passou tempo suficiente e não houve erro, provavelmente o app abriu
        // Mas isso não é 100% confiável, então vamos assumir que pode estar disponível
        resolve(true); // Sempre retorna true - deixar o usuário tentar
      }, 500);
    } catch (error) {
      console.error('Erro ao verificar app nativo:', error);
      resolve(false);
    }
  });
};

/**
 * Pergunta ao usuário se deseja usar o app nativo para rastreamento em background
 * Se sim, abre o app nativo. Se não, continua com o rastreamento web normal.
 */
export const askToUseNativeApp = (
  params: NativeAppTrackingParams,
  onCancel?: () => void
): void => {
  console.log('💬 Perguntando ao usuário sobre app nativo...');
  
  const message = `Deseja usar o app nativo para rastreamento em background?\n\n✅ Vantagens:\n• Rastreamento contínuo mesmo quando minimizado\n• Funciona com tela desligada\n• Melhor economia de bateria\n\n(Se não tiver instalado, continuaremos com o rastreamento web)`;
  
  const userWantsNative = window.confirm(message);
  console.log('👤 Resposta do usuário:', userWantsNative ? 'SIM' : 'NÃO');
  
  if (userWantsNative) {
    console.log('🚀 Usuário escolheu usar app nativo, abrindo...');
    const opened = openNativeApp(params);
    console.log('📱 Resultado da abertura:', opened ? 'Sucesso' : 'Falha');
    
    // Marcar que estamos usando app nativo para reativar periodicamente
    if (opened) {
      const nativeAppKey = `native-app-using-${params.osId}-${params.faseId}`;
      sessionStorage.setItem(nativeAppKey, 'true');
      console.log('✅ App nativo marcado como ativo - será reativado periodicamente');
    }
    
    if (!opened && onCancel) {
      console.log('⚠️ App nativo não disponível, continuando com rastreamento web');
      onCancel();
    }
  } else {
    console.log('ℹ️ Usuário escolheu continuar com rastreamento web');
    if (onCancel) {
      onCancel();
    }
  }
};